import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Plus, Download, Trash2, Search, FileText, 
  Upload, RefreshCw, Check, X, Settings, Image as ImageIcon,
  Edit, ToggleLeft, ToggleRight, User, Loader2, FileEdit, StickyNote, Mail
} from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { CamposPersonalizadosEditor, CamposPersonalizadosInputs, limpiarCampos } from '../../components/admin/CamposPersonalizadosEditor';
import { NotaPaginadaEditor, contenidoInicialNota } from '../../components/admin/notas/NotaPaginadaEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminCertificadosTramitePage() {
  const navigate = useNavigate();
  const [certificados, setCertificados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [activeTab, setActiveTab] = useState('certificados');
  
  // Configuración de sello (único)
  const [selloConfig, setSelloConfig] = useState({ tiene_sello: false, sello_base64: null });
  const [tempSello, setTempSello] = useState('');
  const [guardandoSello, setGuardandoSello] = useState(false);
  
  // Firmas múltiples
  const [firmas, setFirmas] = useState([]);
  const [showFirmaModal, setShowFirmaModal] = useState(false);
  const [editingFirma, setEditingFirma] = useState(null);
  const [firmaForm, setFirmaForm] = useState({ cargo: '', nombre_completo: '', imagen_base64: '' });
  const [guardandoFirma, setGuardandoFirma] = useState(false);
  
  // Legacy - mantener para compatibilidad con código existente
  const configFirmas = { 
    tiene_sello: selloConfig.tiene_sello, 
    tiene_firma: firmas.length > 0 
  };
  
  // Modal de configuración (ahora muestra sello + firmas)
  const [showConfigModal, setShowConfigModal] = useState(false);
  
  // Modal crear certificado
  const [showCrearModal, setShowCrearModal] = useState(false);
  const [creando, setCreando] = useState(false);
  const [tomoFolioOpcional, setTomoFolioOpcional] = useState(false);
  const [firmaSeleccionada, setFirmaSeleccionada] = useState('');
  const [enviarEmailAuto, setEnviarEmailAuto] = useState(true);
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    dni: '',
    mp_numero: '',
    email: '',
    tipo_tramite: 'renovacion_credencial',
    tipo_tramite_otro: '',
    presentar_ante: '',
    // Campos adicionales para certificado_matriculacion
    titulo: '',
    tomo: '',
    folio: '',
    campos_valores: {}
  });
  
  // Tipos de trámite personalizados
  const [tiposTramite, setTiposTramite] = useState([]);
  const [loadingTipos, setLoadingTipos] = useState(false);
  const [showTipoModal, setShowTipoModal] = useState(false);
  const [editingTipo, setEditingTipo] = useState(null);
  const [tipoForm, setTipoForm] = useState({ titulo: '', descripcion: '', requiere_titulo_tomo_folio: false, campos_personalizados: [] });
  const [guardandoTipo, setGuardandoTipo] = useState(false);
  
  // Notas libres
  const [notas, setNotas] = useState([]);
  const [loadingNotas, setLoadingNotas] = useState(false);
  const [showNotaModal, setShowNotaModal] = useState(false);
  const [creandoNota, setCreandoNota] = useState(false);
  const [notaFirmaSeleccionada, setNotaFirmaSeleccionada] = useState('');
  const [notaForm, setNotaForm] = useState({ titulo: '', html: '' });
  const [notaLayout, setNotaLayout] = useState(null);
  const [notaEditorKey, setNotaEditorKey] = useState(0);
  
  
  // Búsqueda de matriculados
  const [busquedaMatriculado, setBusquedaMatriculado] = useState('');
  const [matriculadosEncontrados, setMatriculadosEncontrados] = useState([]);
  const [loadingMatriculados, setLoadingMatriculados] = useState(false);
  const [mostrarResultados, setMostrarResultados] = useState(false);
  const [matriculadoSeleccionado, setMatriculadoSeleccionado] = useState(null);
  
  // Refs para inputs de archivo
  const selloInputRef = useRef(null);
  const firmaInputRef = useRef(null);
  const busquedaRef = useRef(null);
  
  // Cargar certificados y configuración
  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [certRes, selloRes, firmasRes, tiposRes] = await Promise.all([
        axios.get(`${API_URL}/certificados-tramite/admin/listar`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/certificados-tramite/admin/sello`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/certificados-tramite/admin/firmas`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/certificados-tramite/admin/tipos-tramite`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setCertificados(certRes.data);
      setSelloConfig(selloRes.data);
      setFirmas(firmasRes.data);
      setTiposTramite(tiposRes.data);
    } catch (err) {
      console.error('Error cargando datos:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Cargar solo tipos de trámite
  const fetchTiposTramite = async () => {
    setLoadingTipos(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/certificados-tramite/admin/tipos-tramite`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTiposTramite(res.data);
    } catch (err) {
      console.error('Error cargando tipos:', err);
    } finally {
      setLoadingTipos(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, []);
  
  // Cargar notas
  const fetchNotas = async () => {
    setLoadingNotas(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/notas-libres/admin/listar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotas(res.data);
    } catch (err) {
      console.error('Error cargando notas:', err);
    } finally {
      setLoadingNotas(false);
    }
  };
  
  // Cargar notas cuando se cambia a esa tab
  useEffect(() => {
    if (activeTab === 'notas') {
      fetchNotas();
    }
  }, [activeTab]);
  
  const abrirNuevaNota = async () => {
    if (!notaLayout) {
      const res = await axios.get(`${API_URL}/notas-libres/layout`);
      setNotaLayout(res.data);
    }
    setNotaForm({ titulo: '', html: '' });
    setNotaEditorKey(k => k + 1);
    setShowNotaModal(true);
  };

  const notaVacia = !notaForm.html.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();

  // Crear nota
  const handleCrearNota = async () => {
    if (notaVacia) {
      alert('Completá el contenido de la nota');
      return;
    }
    if (!notaFirmaSeleccionada) {
      alert('Seleccioná una firma para la nota');
      return;
    }
    setCreandoNota(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/notas-libres/admin/crear`, {
        titulo: notaForm.titulo,
        contenido_html: notaForm.html,
        firma_id: notaFirmaSeleccionada,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.pdf_base64) {
        const link = document.createElement('a');
        link.href = `data:application/pdf;base64,${res.data.pdf_base64}`;
        link.download = `nota_${res.data.nota_id}.pdf`;
        link.click();
      }
      setShowNotaModal(false);
      setNotaForm({ titulo: '', html: '' });
      setNotaFirmaSeleccionada('');
      fetchNotas();
      alert('Nota creada exitosamente');
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al crear nota');
    } finally {
      setCreandoNota(false);
    }
  };
  
  const handleDescargarNota = async (notaId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/notas-libres/admin/${notaId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.pdf_base64) {
        const link = document.createElement('a');
        link.href = `data:application/pdf;base64,${res.data.pdf_base64}`;
        link.download = `nota_${notaId}.pdf`;
        link.click();
      }
    } catch (err) {
      alert('Error al descargar nota');
    }
  };
  
  // Eliminar nota
  const handleEliminarNota = async (notaId) => {
    if (!window.confirm('¿Eliminar esta nota?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/notas-libres/admin/${notaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotas();
    } catch (err) {
      alert('Error al eliminar nota');
    }
  };
  
  // Buscar matriculados con usuario creado
  const buscarMatriculados = async (texto) => {
    if (!texto || texto.length < 2) {
      setMatriculadosEncontrados([]);
      return;
    }
    
    setLoadingMatriculados(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/antuar/usuarios-registrados`, {
        params: { buscar: texto },
        headers: { Authorization: `Bearer ${token}` }
      });
      setMatriculadosEncontrados(res.data.usuarios || []);
      setMostrarResultados(true);
    } catch (err) {
      console.error('Error buscando matriculados:', err);
    } finally {
      setLoadingMatriculados(false);
    }
  };
  
  // Debounce para búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      if (busquedaMatriculado.length >= 2) {
        buscarMatriculados(busquedaMatriculado);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [busquedaMatriculado]);
  
  // Seleccionar matriculado
  const seleccionarMatriculado = (matriculado) => {
    setMatriculadoSeleccionado(matriculado);
    
    // Separar nombre_completo en apellido y nombre (viene como "Apellido Nombre")
    const nombreCompleto = matriculado.nombre_completo || '';
    const partes = nombreCompleto.split(' ');
    const apellido = partes[0] || '';
    const nombre = partes.slice(1).join(' ') || '';
    
    setFormData({
      ...formData,
      nombre: nombre,
      apellido: apellido,
      dni: matriculado.documento || '',
      mp_numero: matriculado.matricula || '',
      email: matriculado.email || ''
    });
    setBusquedaMatriculado(nombreCompleto);
    setMostrarResultados(false);
  };
  
  // Limpiar selección
  const limpiarSeleccionMatriculado = () => {
    setMatriculadoSeleccionado(null);
    setBusquedaMatriculado('');
    setFormData({
      ...formData,
      nombre: '',
      apellido: '',
      dni: '',
      mp_numero: '',
      email: ''
    });
  };
  
  // Funciones CRUD para tipos de trámite
  const handleNuevoTipo = () => {
    setEditingTipo(null);
    setTipoForm({ titulo: '', descripcion: '', requiere_titulo_tomo_folio: false, campos_personalizados: [] });
    setShowTipoModal(true);
  };
  
  const handleEditarTipo = (tipo) => {
    setEditingTipo(tipo);
    setTipoForm({ 
      titulo: tipo.titulo, 
      descripcion: tipo.descripcion || '',
      requiere_titulo_tomo_folio: tipo.requiere_titulo_tomo_folio || false,
      campos_personalizados: tipo.campos_personalizados || []
    });
    setShowTipoModal(true);
  };
  
  const PLACEHOLDERS_BASE = ['nombre', 'apellido', 'nombre_completo', 'dni', 'DNI', 'mp', 'MP', 'mp_numero', 'presentar_ante', 'titulo', 'tomo', 'folio', 'email'];
  const placeholdersDesconocidos = [...new Set([...(tipoForm.descripcion || '').matchAll(/<<([^<>]+)>>/g)].map(m => m[1]))]
    .filter(k => !PLACEHOLDERS_BASE.includes(k) && !tipoForm.campos_personalizados.some(c => c.key === k));

  const handleGuardarTipo = async () => {
    if (!tipoForm.titulo.trim()) {
      alert('El título es obligatorio');
      return;
    }
    
    if (tipoForm.campos_personalizados.some(c => !c.key || !c.label.trim())) {
      alert('Cada campo personalizado necesita nombre e identificador');
      return;
    }
    setGuardandoTipo(true);
    try {
      const token = localStorage.getItem('token');
      const payloadTipo = { ...tipoForm, campos_personalizados: limpiarCampos(tipoForm.campos_personalizados) };
      
      if (editingTipo) {
        // Actualizar
        await axios.put(
          `${API_URL}/certificados-tramite/admin/tipos-tramite/${editingTipo.id}`,
          payloadTipo,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Crear nuevo
        await axios.post(
          `${API_URL}/certificados-tramite/admin/tipos-tramite`,
          payloadTipo,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      setShowTipoModal(false);
      fetchTiposTramite();
    } catch (err) {
      console.error('Error guardando tipo:', err);
      const det = err.response?.data?.detail;
      alert(Array.isArray(det) ? det.map(d => d.msg).join('\n') : (det || 'Error al guardar'));
    } finally {
      setGuardandoTipo(false);
    }
  };
  
  const handleToggleTipoActivo = async (tipo) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/certificados-tramite/admin/tipos-tramite/${tipo.id}`,
        { activo: !tipo.activo },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTiposTramite();
    } catch (err) {
      console.error('Error actualizando tipo:', err);
      alert(err.response?.data?.detail || 'Error al actualizar');
    }
  };
  
  const handleEliminarTipo = async (tipo) => {
    if (!confirm(`¿Eliminar el tipo de trámite "${tipo.titulo}"?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/certificados-tramite/admin/tipos-tramite/${tipo.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTiposTramite();
    } catch (err) {
      console.error('Error eliminando tipo:', err);
      alert(err.response?.data?.detail || 'Error al eliminar');
    }
  };
  
  // Manejar cambio de imagen
  const handleImageChange = (e, setFn) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert('La imagen no debe superar los 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFn(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Guardar sello
  const handleGuardarSello = async () => {
    if (!tempSello) {
      alert('Seleccioná una imagen de sello');
      return;
    }
    setGuardandoSello(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/certificados-tramite/admin/sello`, 
        { sello_base64: tempSello },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('Sello guardado exitosamente');
      setTempSello('');
      fetchData();
    } catch (err) {
      alert('Error al guardar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setGuardandoSello(false);
    }
  };
  
  // Abrir modal para agregar/editar firma
  const handleAbrirFirmaModal = (firma = null) => {
    if (firma) {
      setEditingFirma(firma);
      setFirmaForm({
        cargo: firma.cargo,
        nombre_completo: firma.nombre_completo,
        imagen_base64: firma.imagen_base64
      });
    } else {
      setEditingFirma(null);
      setFirmaForm({ cargo: '', nombre_completo: '', imagen_base64: '' });
    }
    setShowFirmaModal(true);
  };
  
  // Guardar firma (crear o actualizar)
  const handleGuardarFirma = async () => {
    if (!firmaForm.cargo || !firmaForm.nombre_completo || !firmaForm.imagen_base64) {
      alert('Completá todos los campos de la firma');
      return;
    }
    
    setGuardandoFirma(true);
    try {
      const token = localStorage.getItem('token');
      
      if (editingFirma) {
        await axios.put(
          `${API_URL}/certificados-tramite/admin/firmas/${editingFirma.id}`,
          firmaForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API_URL}/certificados-tramite/admin/firmas`,
          firmaForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      alert(editingFirma ? 'Firma actualizada' : 'Firma agregada exitosamente');
      setShowFirmaModal(false);
      setFirmaForm({ cargo: '', nombre_completo: '', imagen_base64: '' });
      setEditingFirma(null);
      fetchData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    } finally {
      setGuardandoFirma(false);
    }
  };
  
  // Eliminar firma
  const handleEliminarFirma = async (firma) => {
    if (!confirm(`¿Eliminar la firma de ${firma.nombre_completo}?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/certificados-tramite/admin/firmas/${firma.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    }
  };
  
  // Manejar cambio de imagen para firma
  const handleFirmaImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert('La imagen no debe superar los 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFirmaForm({ ...firmaForm, imagen_base64: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Crear certificado
  const handleCrearCertificado = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre || !formData.apellido || !formData.dni || !formData.email || !formData.presentar_ante) {
      alert('Por favor completá todos los campos obligatorios');
      return;
    }
    
    if (formData.tipo_tramite === 'otro' && !formData.tipo_tramite_otro) {
      alert('Por favor especificá el tipo de trámite');
      return;
    }
    
    // Validaciones adicionales para certificado_matriculacion
    if (formData.tipo_tramite === 'certificado_matriculacion') {
      if (!formData.mp_numero) {
        alert('El número de MP es obligatorio para el Certificado de Matriculación');
        return;
      }
      if (!formData.titulo) {
        alert('El Título es obligatorio para el Certificado de Matriculación');
        return;
      }
      if (!tomoFolioOpcional && (!formData.tomo || !formData.folio)) {
        alert('El Tomo y Folio son obligatorios para el Certificado de Matriculación (o desactivá la obligatoriedad)');
        return;
      }
    }
    
    const camposTipo = tiposTramite.find(t => t.id === formData.tipo_tramite)?.campos_personalizados || [];
    const faltante = camposTipo.find(c => c.requerido && !String(formData.campos_valores?.[c.key] || '').trim());
    if (faltante) {
      alert(`Completá el campo "${faltante.label}"`);
      return;
    }

    if (!selloConfig.tiene_sello) {
      alert('Primero debés configurar el sello en la sección de Configuración');
      return;
    }
    
    if (firmas.length === 0) {
      alert('Primero debés agregar al menos una firma en la sección de Configuración');
      return;
    }
    
    if (!firmaSeleccionada) {
      alert('Seleccioná quién firma el certificado');
      return;
    }
    
    setCreando(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/certificados-tramite/admin/crear`, 
        { ...formData, firma_id: firmaSeleccionada, enviar_email: enviarEmailAuto },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { email_enviado, error_email } = response.data;
      
      if (enviarEmailAuto) {
        if (email_enviado) {
          alert(`✅ Certificado creado y enviado por email a ${formData.email}`);
        } else if (error_email) {
          alert(`⚠️ Certificado creado, pero hubo un error al enviar el email: ${error_email}\n\nPodés descargarlo manualmente desde la lista.`);
        } else {
          alert('Certificado creado exitosamente');
        }
      } else {
        alert('✅ Certificado creado exitosamente (sin enviar email)');
      }
      
      setShowCrearModal(false);
      limpiarSeleccionMatriculado();
      setFormData({
        nombre: '',
        apellido: '',
        dni: '',
        mp_numero: '',
        email: '',
        tipo_tramite: 'renovacion_credencial',
        tipo_tramite_otro: '',
        presentar_ante: '',
        titulo: '',
        tomo: '',
        folio: '',
        campos_valores: {}
      });
      setTomoFolioOpcional(false);
      setFirmaSeleccionada('');
      setEnviarEmailAuto(true);
      fetchData();
    } catch (err) {
      alert('Error al crear certificado: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCreando(false);
    }
  };
  
  // Descargar certificado
  const handleDescargar = async (certId, dni) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/certificados-tramite/admin/${certId}/descargar`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificado_tramite_${dni}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Error al descargar: ' + (err.response?.data?.detail || err.message));
    }
  };
  
  // Eliminar certificado
  const handleEliminar = async (certId) => {
    if (!window.confirm('¿Estás seguro de eliminar este certificado?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/certificados-tramite/admin/${certId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchData();
    } catch (err) {
      alert('Error al eliminar: ' + (err.response?.data?.detail || err.message));
    }
  };
  
  // Filtrar certificados
  const certificadosFiltrados = certificados.filter(cert => {
    const matchSearch = 
      cert.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cert.apellido?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cert.dni?.includes(searchTerm) ||
      cert.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchTipo = filtroTipo === 'todos' || cert.tipo_tramite === filtroTipo;
    
    return matchSearch && matchTipo;
  });
  
  // Obtener etiqueta de tipo de trámite
  const getTipoTramiteLabel = (tipo) => {
    const tiposPredefinidos = {
      'renovacion_credencial': { label: 'Renovación', color: 'bg-blue-100 text-blue-700' },
      'matriculacion': { label: 'Matriculación', color: 'bg-green-100 text-green-700' },
      'certificado_matriculacion': { label: 'Cert. Matriculación', color: 'bg-emerald-100 text-emerald-700' },
      'otro': { label: 'Otro', color: 'bg-gray-100 text-gray-700' }
    };
    
    if (tiposPredefinidos[tipo]) {
      return tiposPredefinidos[tipo];
    }
    
    // Buscar en tipos personalizados
    const tipoPersonalizado = tiposTramite.find(t => t.id === tipo);
    if (tipoPersonalizado) {
      return { label: tipoPersonalizado.titulo, color: 'bg-purple-100 text-purple-700' };
    }
    
    return { label: tipo, color: 'bg-gray-100 text-gray-700' };
  };
  
  // Obtener opciones de tipo para el select del filtro
  const getOpcionesTipoFiltro = () => {
    const opciones = [
      { value: 'todos', label: 'Todos los tipos' },
      { value: 'renovacion_credencial', label: 'Renovación de Credencial' },
      { value: 'matriculacion', label: 'Matriculación Profesional' },
      { value: 'certificado_matriculacion', label: 'Cert. Matriculación' },
    ];
    
    // Agregar tipos personalizados activos
    tiposTramite.filter(t => !t.predefinido && t.activo).forEach(t => {
      opciones.push({ value: t.id, label: t.titulo });
    });
    
    opciones.push({ value: 'otro', label: 'Otro' });
    
    return opciones;
  };

  // Verificar si el tipo de trámite actual requiere campos de título/tomo/folio
  const requiereTituloTomoFolio = () => {
    // Certificado de matriculación siempre lo requiere
    if (formData.tipo_tramite === 'certificado_matriculacion') {
      return true;
    }
    // Buscar en tipos personalizados
    const tipoPersonalizado = tiposTramite.find(t => t.id === formData.tipo_tramite);
    return tipoPersonalizado?.requiere_titulo_tomo_folio || false;
  };
  
  return (
    <TooltipProvider>
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/admin')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Gestión de Certificados</h1>
                <p className="text-gray-500">Gestión de certificados para matriculados</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowConfigModal(true)}
                className={!configFirmas.tiene_sello || !configFirmas.tiene_firma ? 'border-amber-400 text-amber-700' : ''}
              >
                <Settings className="w-4 h-4 mr-2" />
                Configurar Sello/Firma
                {(!configFirmas.tiene_sello || !configFirmas.tiene_firma) && (
                  <span className="ml-2 w-2 h-2 bg-amber-500 rounded-full"></span>
                )}
              </Button>
            </div>
          </div>
          
          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList>
              <TabsTrigger value="certificados">Certificados</TabsTrigger>
              <TabsTrigger value="notas">Notas Libres</TabsTrigger>
              <TabsTrigger value="tipos">Tipos de Trámite</TabsTrigger>
            </TabsList>
            
            {/* Tab Certificados */}
            <TabsContent value="certificados" className="space-y-4">
          {/* Alerta si no hay sello/firma configurados */}
          {(!configFirmas.tiene_sello || !configFirmas.tiene_firma) && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-center gap-3">
              <ImageIcon className="w-5 h-5 text-amber-600" />
              <div>
                <p className="text-amber-800 font-medium">Configuración incompleta</p>
                <p className="text-amber-600 text-sm">
                  {!configFirmas.tiene_sello && !configFirmas.tiene_firma 
                    ? 'Debés subir el sello y la firma del presidente antes de crear certificados.'
                    : !configFirmas.tiene_sello 
                    ? 'Falta configurar el sello CATSLZ.'
                    : 'Falta configurar la firma del presidente.'}
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => setShowConfigModal(true)} className="ml-auto">
                Configurar ahora
              </Button>
            </div>
          )}
          
          {/* Filtros */}
          <Card className="mb-6">
            <CardContent className="py-4">
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex-1 min-w-[250px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Buscar por nombre, DNI o email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <Select value={filtroTipo} onValueChange={setFiltroTipo}>
                  <SelectTrigger className="w-[220px]">
                    <SelectValue placeholder="Filtrar por tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {getOpcionesTipoFiltro().map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={fetchData}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
                <Button onClick={() => setShowCrearModal(true)} className="bg-violet-600 hover:bg-violet-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Certificado
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Tabla de certificados */}
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : certificadosFiltrados.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No hay certificados registrados</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nombre</TableHead>
                      <TableHead>DNI</TableHead>
                      <TableHead>MP Nº</TableHead>
                      <TableHead>Tipo de Trámite</TableHead>
                      <TableHead>Presentar ante</TableHead>
                      <TableHead>Firmante</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {certificadosFiltrados.map((cert) => {
                      const tipoInfo = getTipoTramiteLabel(cert.tipo_tramite);
                      return (
                        <TableRow key={cert.id}>
                          <TableCell className="font-medium">
                            {cert.nombre} {cert.apellido}
                          </TableCell>
                          <TableCell>{cert.dni}</TableCell>
                          <TableCell>{cert.mp_numero || '-'}</TableCell>
                          <TableCell>
                            <Badge className={tipoInfo.color}>
                              {cert.tipo_tramite_texto || tipoInfo.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-[150px] truncate" title={cert.presentar_ante}>
                            {cert.presentar_ante}
                          </TableCell>
                          <TableCell className="text-sm">
                            {cert.firma_nombre ? (
                              <Tooltip>
                                <TooltipTrigger>
                                  <span className="text-violet-600 cursor-help">
                                    {cert.firma_nombre.split(' ').slice(-1)[0]}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <div className="text-center">
                                    <p className="font-medium">{cert.firma_nombre}</p>
                                    <p className="text-xs text-gray-400 capitalize">{cert.firma_cargo}</p>
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-gray-500">
                            {cert.fecha_creacion ? new Date(cert.fecha_creacion).toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' }) : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => handleDescargar(cert.id, cert.dni)}
                                  >
                                    <Download className="w-4 h-4 text-blue-600" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Descargar PDF</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => handleEliminar(cert.id)}
                                  >
                                    <Trash2 className="w-4 h-4 text-red-600" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Eliminar</TooltipContent>
                              </Tooltip>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
          
          {/* Estadísticas */}
          <div className="mt-4 text-sm text-gray-500 text-center">
            Mostrando {certificadosFiltrados.length} de {certificados.length} certificados
          </div>
            </TabsContent>
            
            {/* Tab Notas Libres */}
            <TabsContent value="notas" className="space-y-4">
              {/* Alerta si no hay sello/firma configurados */}
              {(!configFirmas.tiene_sello || !configFirmas.tiene_firma) && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-center gap-3">
                  <ImageIcon className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="text-amber-800 font-medium">Configuración incompleta</p>
                    <p className="text-amber-600 text-sm">
                      Debés configurar el sello y al menos una firma antes de crear notas.
                    </p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setShowConfigModal(true)} className="ml-auto">
                    Configurar ahora
                  </Button>
                </div>
              )}
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <StickyNote className="w-5 h-5" />
                      Notas Libres
                    </CardTitle>
                    <p className="text-sm text-gray-500 mt-1">
                      Creá notas en hojas con membrete. Ubicás el bloque de sello, firma y QR donde quieras con "Insertar firma" (si no, va al final).
                    </p>
                  </div>
                  <Button 
                    onClick={abrirNuevaNota} 
                    className="bg-violet-600 hover:bg-violet-700"
                    disabled={!configFirmas.tiene_sello || !configFirmas.tiene_firma}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nueva Nota
                  </Button>
                </CardHeader>
                <CardContent>
                  {loadingNotas ? (
                    <div className="text-center py-8 text-gray-500">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                      Cargando notas...
                    </div>
                  ) : notas.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <StickyNote className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>No hay notas creadas todavía</p>
                      <p className="text-sm">Hacé clic en "Nueva Nota" para crear tu primera nota libre</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Fecha</TableHead>
                          <TableHead>Título</TableHead>
                          <TableHead>Destinatario</TableHead>
                          <TableHead>Páginas</TableHead>
                          <TableHead>Creado por</TableHead>
                          <TableHead className="text-right">Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {notas.map(nota => (
                          <TableRow key={nota.id}>
                            <TableCell className="text-sm text-gray-600">
                              {new Date(nota.fecha_creacion).toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}
                            </TableCell>
                            <TableCell className="font-medium">{nota.titulo}</TableCell>
                            <TableCell className="text-sm text-gray-600">
                              {nota.destinatario || '-'}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{nota.paginas_estimadas} pág.</Badge>
                            </TableCell>
                            <TableCell className="text-sm text-gray-600">
                              {nota.creado_por}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => handleDescargarNota(nota.id)}
                                    >
                                      <Download className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Descargar PDF</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      className="text-red-600 hover:text-red-700"
                                      onClick={() => handleEliminarNota(nota.id)}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Eliminar</TooltipContent>
                                </Tooltip>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Tab Tipos de Trámite */}
            <TabsContent value="tipos" className="space-y-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg">Tipos de Trámite</CardTitle>
                  <Button onClick={handleNuevoTipo} className="bg-violet-600 hover:bg-violet-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Nuevo Tipo de Trámite
                  </Button>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-500 mb-4">
                    Creá tipos de trámite personalizados con título y descripción. Estos aparecerán como opciones al crear certificados y en el formulario público.
                  </p>
                  
                  {loadingTipos ? (
                    <div className="text-center py-8 text-gray-500">Cargando...</div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Título</TableHead>
                          <TableHead>Descripción</TableHead>
                          <TableHead className="w-[100px]">Estado</TableHead>
                          <TableHead className="w-[100px]">Tipo</TableHead>
                          <TableHead className="text-right w-[120px]">Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {tiposTramite.map((tipo) => (
                          <TableRow key={tipo.id} className={!tipo.activo ? 'opacity-50' : ''}>
                            <TableCell className="font-medium">{tipo.titulo}</TableCell>
                            <TableCell className="max-w-xs truncate text-sm text-gray-500">
                              {tipo.descripcion || '-'}
                            </TableCell>
                            <TableCell>
                              {tipo.activo ? (
                                <Badge className="bg-green-100 text-green-700">Activo</Badge>
                              ) : (
                                <Badge variant="secondary">Inactivo</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {tipo.predefinido ? (
                                <Badge variant="outline" className="text-xs">Predefinido</Badge>
                              ) : (
                                <Badge className="bg-purple-100 text-purple-700 text-xs">Personalizado</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {!tipo.predefinido && (
                                <div className="flex justify-end gap-1">
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={() => handleToggleTipoActivo(tipo)}
                                      >
                                        {tipo.activo ? (
                                          <ToggleRight className="w-4 h-4 text-green-600" />
                                        ) : (
                                          <ToggleLeft className="w-4 h-4 text-gray-400" />
                                        )}
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>{tipo.activo ? 'Desactivar' : 'Activar'}</TooltipContent>
                                  </Tooltip>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={() => handleEditarTipo(tipo)}
                                      >
                                        <Edit className="w-4 h-4 text-blue-600" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Editar</TooltipContent>
                                  </Tooltip>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={() => handleEliminarTipo(tipo)}
                                      >
                                        <Trash2 className="w-4 h-4 text-red-600" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Eliminar</TooltipContent>
                                  </Tooltip>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
        
        {/* Modal Configuración de Firmas */}
        <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-violet-600" />
                Configuración de Sello y Firmas
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Sección Sello */}
              <div className="p-4 border rounded-lg">
                <Label className="flex items-center gap-2 mb-3 text-base font-semibold">
                  Sello CATSLZ (único para todos los certificados)
                  {selloConfig.tiene_sello && <Check className="w-4 h-4 text-green-600" />}
                </Label>
                <input
                  type="file"
                  ref={selloInputRef}
                  accept="image/*"
                  onChange={(e) => handleImageChange(e, setTempSello)}
                  className="hidden"
                />
                
                <div className="flex gap-3 items-start">
                  {selloConfig.sello_base64 && !tempSello && (
                    <div className="p-2 border rounded bg-gray-50">
                      <img src={selloConfig.sello_base64} alt="Sello actual" className="h-20 object-contain" />
                      <p className="text-xs text-green-600 mt-1 text-center">Sello actual</p>
                    </div>
                  )}
                  {tempSello && (
                    <div className="p-2 border rounded bg-green-50 border-green-300">
                      <img src={tempSello} alt="Sello nuevo" className="h-20 object-contain" />
                      <p className="text-xs text-green-600 mt-1 text-center">Nuevo sello</p>
                    </div>
                  )}
                  <div className="flex flex-col gap-2">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => selloInputRef.current?.click()}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {tempSello ? 'Cambiar' : selloConfig.tiene_sello ? 'Reemplazar' : 'Subir sello'}
                    </Button>
                    {tempSello && (
                      <>
                        <Button 
                          onClick={handleGuardarSello} 
                          disabled={guardandoSello}
                          className="bg-green-600 hover:bg-green-700"
                          size="sm"
                        >
                          {guardandoSello ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Guardar Sello'}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setTempSello('')}>
                          <X className="w-4 h-4 mr-1" /> Cancelar
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Sección Firmas */}
              <div className="p-4 border rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-semibold">
                    Firmas Disponibles ({firmas.length})
                  </Label>
                  <Button 
                    size="sm" 
                    onClick={() => handleAbrirFirmaModal()}
                    className="bg-violet-600 hover:bg-violet-700"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Agregar Firma
                  </Button>
                </div>
                
                <p className="text-sm text-gray-600 mb-3">
                  Podés cargar varias firmas (presidente, secretaria, tesorera, etc.) y elegir cuál usar al crear cada certificado.
                </p>
                
                {firmas.length === 0 ? (
                  <div className="text-center py-6 bg-gray-50 rounded-lg">
                    <ImageIcon className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">No hay firmas cargadas</p>
                    <p className="text-xs text-gray-400">Agregá al menos una firma para emitir certificados</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {firmas.map((firma) => (
                      <div key={firma.id} className="p-3 border rounded-lg bg-white flex items-start gap-3">
                        <img 
                          src={firma.imagen_base64} 
                          alt={firma.cargo} 
                          className="h-14 w-14 object-contain border rounded"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{firma.nombre_completo}</p>
                          <p className="text-xs text-gray-500 capitalize">{firma.cargo}</p>
                        </div>
                        <div className="flex flex-col gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-7 w-7 p-0"
                            onClick={() => handleAbrirFirmaModal(firma)}
                          >
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                            onClick={() => handleEliminarFirma(firma)}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowConfigModal(false)}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Modal Agregar/Editar Firma */}
        <Dialog open={showFirmaModal} onOpenChange={setShowFirmaModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingFirma ? 'Editar Firma' : 'Nueva Firma'}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label>Cargo</Label>
                <Select 
                  value={firmaForm.cargo} 
                  onValueChange={(v) => setFirmaForm({...firmaForm, cargo: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar cargo..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="presidente">Presidente</SelectItem>
                    <SelectItem value="secretaria">Secretaria</SelectItem>
                    <SelectItem value="tesorera">Tesorera</SelectItem>
                    <SelectItem value="vocal">Vocal</SelectItem>
                    <SelectItem value="otro">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Nombre Completo</Label>
                <Input
                  value={firmaForm.nombre_completo}
                  onChange={(e) => setFirmaForm({...firmaForm, nombre_completo: e.target.value})}
                  placeholder="Ej: Lic. María González"
                />
              </div>
              
              <div>
                <Label>Imagen de Firma</Label>
                <input
                  type="file"
                  ref={firmaInputRef}
                  accept="image/*"
                  onChange={handleFirmaImageChange}
                  className="hidden"
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  className="w-full mt-1"
                  onClick={() => firmaInputRef.current?.click()}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {firmaForm.imagen_base64 ? 'Cambiar imagen' : 'Subir imagen de firma'}
                </Button>
                {firmaForm.imagen_base64 && (
                  <div className="mt-2 p-3 border rounded-lg bg-gray-50 flex items-center justify-center">
                    <img src={firmaForm.imagen_base64} alt="Preview" className="h-16 object-contain" />
                  </div>
                )}
              </div>
            </div>
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowFirmaModal(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleGuardarFirma}
                disabled={guardandoFirma || !firmaForm.cargo || !firmaForm.nombre_completo || !firmaForm.imagen_base64}
                className="bg-violet-600 hover:bg-violet-700"
              >
                {guardandoFirma ? <RefreshCw className="w-4 h-4 animate-spin" /> : (editingFirma ? 'Actualizar' : 'Guardar')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Modal Crear Certificado */}
        <Dialog open={showCrearModal} onOpenChange={(open) => {
          setShowCrearModal(open);
          if (!open) {
            // Limpiar al cerrar
            limpiarSeleccionMatriculado();
          }
        }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-violet-600" />
                Nuevo Certificado de Inicio de Trámite
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleCrearCertificado} className="space-y-4">
              {/* Buscador de matriculados */}
              <div className="p-4 bg-violet-50 rounded-lg border border-violet-200 space-y-3">
                <Label className="flex items-center gap-2 text-violet-700 font-medium">
                  <User className="w-4 h-4" />
                  Buscar matriculado/a con usuario creado
                </Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    ref={busquedaRef}
                    value={busquedaMatriculado}
                    onChange={(e) => {
                      setBusquedaMatriculado(e.target.value);
                      if (matriculadoSeleccionado && e.target.value !== `${matriculadoSeleccionado.nombre} ${matriculadoSeleccionado.apellido}`) {
                        setMatriculadoSeleccionado(null);
                      }
                    }}
                    onFocus={() => matriculadosEncontrados.length > 0 && setMostrarResultados(true)}
                    placeholder="Buscar por nombre, apellido, matrícula o DNI..."
                    className="pl-10 pr-10"
                  />
                  {loadingMatriculados && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-violet-500" />
                  )}
                  {matriculadoSeleccionado && !loadingMatriculados && (
                    <button
                      type="button"
                      onClick={limpiarSeleccionMatriculado}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  
                  {/* Resultados de búsqueda */}
                  {mostrarResultados && matriculadosEncontrados.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {matriculadosEncontrados.map((m) => (
                        <button
                          key={m.id || m.matricula}
                          type="button"
                          onClick={() => seleccionarMatriculado(m)}
                          className="w-full px-4 py-2 text-left hover:bg-violet-50 border-b last:border-b-0 flex items-center gap-3"
                        >
                          <User className="w-4 h-4 text-violet-500 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{m.nombre_completo}</p>
                            <p className="text-xs text-gray-500">
                              MP {m.matricula} • DNI {m.documento}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {mostrarResultados && busquedaMatriculado.length >= 2 && matriculadosEncontrados.length === 0 && !loadingMatriculados && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg p-4 text-center text-sm text-gray-500">
                      No se encontraron matriculados con esa búsqueda
                    </div>
                  )}
                </div>
                
                {matriculadoSeleccionado && (
                  <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 p-2 rounded">
                    <Check className="w-4 h-4" />
                    Datos cargados de: {matriculadoSeleccionado.nombre_completo}
                  </div>
                )}
                
                <p className="text-xs text-violet-600">
                  También podés completar los datos manualmente si el/la matriculado/a no tiene usuario creado
                </p>
              </div>
              
              {/* Datos personales */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nombre *</Label>
                  <Input
                    value={formData.nombre}
                    onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                    placeholder="Nombre"
                    required
                  />
                </div>
                <div>
                  <Label>Apellido *</Label>
                  <Input
                    value={formData.apellido}
                    onChange={(e) => setFormData({...formData, apellido: e.target.value})}
                    placeholder="Apellido"
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>DNI *</Label>
                  <Input
                    value={formData.dni}
                    onChange={(e) => setFormData({...formData, dni: e.target.value.replace(/\D/g, '')})}
                    placeholder="12345678"
                    required
                  />
                </div>
                {formData.tipo_tramite !== 'certificado_matriculacion' && (
                  <div>
                    <Label>MP Nº (opcional)</Label>
                    <Input
                      value={formData.mp_numero}
                      onChange={(e) => setFormData({...formData, mp_numero: e.target.value})}
                      placeholder="Número de matrícula"
                    />
                  </div>
                )}
              </div>
              
              <div>
                <Label>Correo Electrónico *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="ejemplo@email.com"
                  required
                />
              </div>
              
              {/* Tipo de trámite */}
              <div>
                <Label>Tipo de Trámite *</Label>
                <Select 
                  value={formData.tipo_tramite} 
                  onValueChange={(value) => setFormData({...formData, tipo_tramite: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="renovacion_credencial">Renovación de Credencial</SelectItem>
                    <SelectItem value="matriculacion">Matriculación Profesional</SelectItem>
                    <SelectItem value="certificado_matriculacion">Certificado de Matriculación sin Antecedentes</SelectItem>
                    {/* Tipos personalizados */}
                    {tiposTramite.filter(t => !t.predefinido && t.activo).map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.titulo}</SelectItem>
                    ))}
                    <SelectItem value="otro">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {formData.tipo_tramite === 'otro' && (
                <div>
                  <Label>Especificar tipo de trámite *</Label>
                  <Input
                    value={formData.tipo_tramite_otro}
                    onChange={(e) => setFormData({...formData, tipo_tramite_otro: e.target.value})}
                    placeholder="Describí el tipo de trámite"
                    required
                  />
                </div>
              )}
              
              {/* Campos adicionales para tipos que requieren Título/Tomo/Folio */}
              {requiereTituloTomoFolio() && (
                <div className="space-y-4 p-4 bg-violet-50 rounded-lg border border-violet-200">
                  <p className="text-sm text-violet-700 font-medium">
                    Campos adicionales para este tipo de certificado:
                  </p>
                  
                  {formData.tipo_tramite === 'certificado_matriculacion' && (
                    <div>
                      <Label>MP Nº *</Label>
                      <Input
                        value={formData.mp_numero}
                        onChange={(e) => setFormData({...formData, mp_numero: e.target.value})}
                        placeholder="Número de matrícula"
                        required
                      />
                    </div>
                  )}
                  
                  <div>
                    <Label>Título *</Label>
                    <Input
                      value={formData.titulo}
                      onChange={(e) => setFormData({...formData, titulo: e.target.value})}
                      placeholder="Ej: Licenciado/a en Trabajo Social"
                      required
                    />
                  </div>
                  
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm">Tomo y Folio opcionales</Label>
                    <Switch
                      checked={tomoFolioOpcional}
                      onCheckedChange={setTomoFolioOpcional}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Tomo {!tomoFolioOpcional && '*'}</Label>
                      <Input
                        value={formData.tomo}
                        onChange={(e) => setFormData({...formData, tomo: e.target.value})}
                        placeholder="Tomo"
                        required={!tomoFolioOpcional}
                      />
                    </div>
                    <div>
                      <Label>Folio {!tomoFolioOpcional && '*'}</Label>
                      <Input
                        value={formData.folio}
                        onChange={(e) => setFormData({...formData, folio: e.target.value})}
                        placeholder="Folio"
                        required={!tomoFolioOpcional}
                      />
                    </div>
                  </div>
                </div>
              )}
              
              <CamposPersonalizadosInputs
                campos={tiposTramite.find(t => t.id === formData.tipo_tramite)?.campos_personalizados}
                valores={formData.campos_valores}
                onChange={(v) => setFormData({ ...formData, campos_valores: v })}
              />

              {/* Presentar ante */}
              <div>
                <Label>Para ser presentado ante *</Label>
                <Input
                  value={formData.presentar_ante}
                  onChange={(e) => setFormData({...formData, presentar_ante: e.target.value})}
                  placeholder="Ej: Ministerio de Salud, ANSES, etc."
                  required
                />
              </div>
              
              {/* Selector de Firma */}
              <div>
                <Label>¿Quién firma? *</Label>
                {firmas.length > 0 ? (
                  <Select 
                    value={firmaSeleccionada} 
                    onValueChange={setFirmaSeleccionada}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar firmante..." />
                    </SelectTrigger>
                    <SelectContent>
                      {firmas.map((firma) => (
                        <SelectItem key={firma.id} value={firma.id}>
                          {firma.nombre_completo} ({firma.cargo})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-sm text-amber-600 mt-1">
                    No hay firmas configuradas. Agregá al menos una en Configuración.
                  </p>
                )}
              </div>
              
              {/* Info de configuración */}
              <div className={`p-3 rounded-lg text-sm ${selloConfig.tiene_sello && firmas.length > 0 ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                {selloConfig.tiene_sello && firmas.length > 0 ? (
                  <p>✓ El certificado usará el sello configurado y la firma seleccionada</p>
                ) : (
                  <p>⚠️ Configurá el sello y al menos una firma antes de crear certificados</p>
                )}
              </div>

              {/* Opción de enviar email */}
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-blue-600" />
                  <div>
                    <Label className="text-sm font-medium text-blue-800">Enviar email automáticamente</Label>
                    <p className="text-xs text-blue-600">
                      {enviarEmailAuto 
                        ? `Se enviará el certificado a ${formData.email || 'el email indicado'}` 
                        : 'El certificado no se enviará por email'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={enviarEmailAuto}
                  onCheckedChange={setEnviarEmailAuto}
                />
              </div>
              
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setShowCrearModal(false)}>
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  disabled={creando || !selloConfig.tiene_sello || firmas.length === 0 || !firmaSeleccionada} 
                  className="bg-violet-600 hover:bg-violet-700"
                >
                  {creando ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Creando...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Crear Certificado
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        
        {/* Modal Crear/Editar Tipo de Trámite */}
        <Dialog open={showTipoModal} onOpenChange={setShowTipoModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingTipo ? 'Editar Tipo de Trámite' : 'Nuevo Tipo de Trámite Personalizado'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label>Título del Certificado *</Label>
                <Input
                  value={tipoForm.titulo}
                  onChange={(e) => setTipoForm({...tipoForm, titulo: e.target.value})}
                  placeholder="Ej: Constancia de Matriculación"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Este será el título que aparece en el PDF del certificado
                </p>
              </div>
              
              <div>
                <Label>Texto del Certificado *</Label>
                <Textarea
                  value={tipoForm.descripcion}
                  onChange={(e) => setTipoForm({...tipoForm, descripcion: e.target.value})}
                  placeholder="Ej: Se deja constancia que <<nombre>> <<apellido>>, DNI <<dni>>, se encuentra matriculado bajo la matrícula MP <<mp>>..."
                  rows={6}
                />
                <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-800 font-medium mb-1">Códigos disponibles (se reemplazan automáticamente):</p>
                  <div className="grid grid-cols-2 gap-1 text-xs text-blue-700">
                    <span><code className="bg-blue-100 px-1 rounded">{`<<nombre>>`}</code> Nombre</span>
                    <span><code className="bg-blue-100 px-1 rounded">{`<<apellido>>`}</code> Apellido</span>
                    <span><code className="bg-blue-100 px-1 rounded">{`<<dni>>`}</code> DNI</span>
                    <span><code className="bg-blue-100 px-1 rounded">{`<<mp>>`}</code> Nº Matrícula</span>
                    <span><code className="bg-blue-100 px-1 rounded">{`<<email>>`}</code> Email</span>
                    <span><code className="bg-blue-100 px-1 rounded">{`<<presentar_ante>>`}</code> Destino</span>
                    {placeholdersDesconocidos.length > 0 && (
                      <span className="col-span-2 text-amber-700" data-testid="tipo-placeholders-desconocidos">
                        Atención: {placeholdersDesconocidos.map(k => `<<${k}>>`).join(', ')} no coincide con ningún campo y quedará tal cual en el PDF.
                      </span>
                    )}
                    {tipoForm.campos_personalizados.filter(c => c.key).map(c => (
                      <span key={c.key}><code className="bg-violet-100 px-1 rounded">{`<<${c.key}>>`}</code> {c.label}</span>
                    ))}
                    {tipoForm.requiere_titulo_tomo_folio && (
                      <>
                        <span><code className="bg-green-100 px-1 rounded">{`<<titulo>>`}</code> Título</span>
                        <span><code className="bg-green-100 px-1 rounded">{`<<tomo>>`}</code> Tomo</span>
                        <span><code className="bg-green-100 px-1 rounded">{`<<folio>>`}</code> Folio</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <CamposPersonalizadosEditor
                campos={tipoForm.campos_personalizados}
                onChange={(campos) => setTipoForm({ ...tipoForm, campos_personalizados: campos })}
              />

              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  id="requiere_titulo_tomo_folio"
                  checked={tipoForm.requiere_titulo_tomo_folio}
                  onChange={(e) => setTipoForm({...tipoForm, requiere_titulo_tomo_folio: e.target.checked})}
                  className="h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                />
                <div>
                  <Label htmlFor="requiere_titulo_tomo_folio" className="cursor-pointer">
                    Solicitar Título, Tomo y Folio
                  </Label>
                  <p className="text-xs text-gray-500">
                    Al crear el certificado, se pedirán estos campos adicionales (como en certificado de matriculación sin antecedentes)
                  </p>
                </div>
              </div>
            </div>
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowTipoModal(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleGuardarTipo}
                disabled={guardandoTipo || !tipoForm.titulo.trim() || !tipoForm.descripcion.trim()}
                className="bg-violet-600 hover:bg-violet-700"
              >
                {guardandoTipo ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    {editingTipo ? 'Guardar Cambios' : 'Crear Tipo'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Modal Crear Nota (editor por páginas) */}
        <Dialog open={showNotaModal} onOpenChange={setShowNotaModal}>
          <DialogContent className="max-w-[96vw] w-[96vw] max-h-[95vh] overflow-y-auto overflow-x-hidden" data-testid="nota-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <StickyNote className="w-5 h-5" />
                Nueva Nota Libre
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 min-w-0">
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label>Título (solo para identificarla en el listado y en la verificación)</Label>
                  <Input value={notaForm.titulo} data-testid="nota-titulo-input"
                    onChange={(e) => setNotaForm({ ...notaForm, titulo: e.target.value })}
                    placeholder="Ej: Nota de Presentación, Comunicación Oficial..." />
                </div>
                <div>
                  <Label>Firma *</Label>
                  <Select value={notaFirmaSeleccionada} onValueChange={setNotaFirmaSeleccionada}>
                    <SelectTrigger data-testid="nota-firma-select">
                      <SelectValue placeholder="Seleccioná quién firma" />
                    </SelectTrigger>
                    <SelectContent>
                      {firmas.map(f => (
                        <SelectItem key={f.id} value={f.id}>{f.nombre_completo} - {f.cargo}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {firmas.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">No hay firmas configuradas. Configurá al menos una firma primero.</p>
                  )}
                </div>
              </div>
              {notaLayout && (
                <NotaPaginadaEditor
                  key={notaEditorKey}
                  layout={notaLayout.layout}
                  css={notaLayout.css}
                  initialHtml={contenidoInicialNota()}
                  onChange={(html) => setNotaForm(f => ({ ...f, html }))}
                  selloSrc={selloConfig.sello_base64}
                  firmaSrc={firmas.find(f => f.id === notaFirmaSeleccionada)?.imagen_base64}
                />
              )}
              <p className="text-xs text-gray-500">
                Las hojas impares llevan el membrete del Colegio y las pares van en blanco. Con "Insertar firma" ubicás el bloque de sello, firma y QR debajo del renglón donde está el cursor (si no lo insertás, va al final). El PDF respeta este mismo armado.
              </p>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowNotaModal(false)} data-testid="nota-cancelar-btn">Cancelar</Button>
              <Button onClick={handleCrearNota} disabled={creandoNota || notaVacia || !notaFirmaSeleccionada}
                className="bg-violet-600 hover:bg-violet-700" data-testid="nota-crear-btn">
                {creandoNota ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generando PDF...</>) : (<><FileText className="w-4 h-4 mr-2" />Crear y Descargar PDF</>)}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
